import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Paper,
  Badge,
  Tabs,
  Tab,
  Divider,
  alpha,
  useTheme
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  Person as PersonIcon,
  CleaningServices as CleanerIcon,
  AdminPanelSettings as AdminIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

// Mock data for demonstration
const mockUsers = [
  {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.j@example.com",
    type: "Customer",
    status: "Active",
    joinDate: "2024-01-15",
    lastActive: "2024-03-20",
    avatar: "/static/images/avatar/1.jpg",
    phone: "+1 (555) 123-4567",
    jobsCompleted: 12,
    rating: 4.8
  },
  {
    id: 2,
    name: "Mike Chen",
    email: "mike.chen@example.com",
    type: "Cleaner",
    status: "Active",
    joinDate: "2024-02-10",
    lastActive: "2024-03-21",
    avatar: "/static/images/avatar/2.jpg",
    phone: "+1 (555) 987-6543",
    jobsCompleted: 47,
    rating: 4.9
  },
  {
    id: 3,
    name: "Emily Davis",
    email: "emily.d@example.com",
    type: "Customer",
    status: "Inactive",
    joinDate: "2024-01-20",
    lastActive: "2024-02-15",
    avatar: "/static/images/avatar/3.jpg",
    phone: "+1 (555) 456-7890",
    jobsCompleted: 3,
    rating: 4.5
  },
  {
    id: 4,
    name: "Carlos Rodriguez",
    email: "c.rodriguez@example.com",
    type: "Cleaner",
    status: "Pending",
    joinDate: "2024-03-01",
    lastActive: "2024-03-21",
    avatar: "/static/images/avatar/4.jpg",
    phone: "+1 (555) 234-5678",
    jobsCompleted: 0,
    rating: null
  },
  {
    id: 5,
    name: "Admin User",
    email: "admin@freshsweeper.com",
    type: "Admin",
    status: "Active",
    joinDate: "2024-01-01",
    lastActive: "2024-03-21",
    avatar: "/static/images/avatar/5.jpg",
    phone: "+1 (555) 345-6789",
    jobsCompleted: 0,
    rating: null
  }
];

const UserTypes = {
  ALL: 'all',
  CUSTOMER: 'Customer',
  CLEANER: 'Cleaner',
  ADMIN: 'Admin'
};

const StatusTypes = {
  ALL: 'all',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  PENDING: 'Pending'
};

export default function Users() {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState(0);
  const [users, setUsers] = useState(mockUsers);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [userTypeFilter, setUserTypeFilter] = useState(UserTypes.ALL);
  const [statusFilter, setStatusFilter] = useState(StatusTypes.ALL);

  // Filter users based on search term and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = userTypeFilter === UserTypes.ALL || user.type === userTypeFilter;
    const matchesStatus = statusFilter === StatusTypes.ALL || user.status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredUsers.map((n) => n.id);
      setSelectedUsers(newSelecteds);
      return;
    }
    setSelectedUsers([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedUsers.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedUsers, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedUsers.slice(1));
    } else if (selectedIndex === selectedUsers.length - 1) {
      newSelected = newSelected.concat(selectedUsers.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedUsers.slice(0, selectedIndex),
        selectedUsers.slice(selectedIndex + 1),
      );
    }

    setSelectedUsers(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selectedUsers.indexOf(id) !== -1;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'error';
      case 'Pending': return 'warning';
      default: return 'default';
    }
  };

  const getUserTypeIcon = (type) => {
    switch (type) {
      case 'Cleaner': return <CleanerIcon />;
      case 'Admin': return <AdminIcon />;
      default: return <PersonIcon />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active': return <ActiveIcon />;
      case 'Inactive': return <BlockIcon />;
      default: return <MoreIcon />;
    }
  };

  const handleActionMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFilterMenuOpen = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    // In a real app, you would filter users based on the tab
  };

  const stats = [
    { label: 'Total Users', value: '1,254', color: 'primary' },
    { label: 'Active Today', value: '89', color: 'success' },
    { label: 'New This Week', value: '34', color: 'warning' },
    { label: 'Pending Verification', value: '12', color: 'error' },
  ];

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            User Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            Add New User
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage all registered users, their permissions, and activity
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: `1px solid ${theme.palette.divider}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" fontWeight="bold" color={`${stat.color}.main`} gutterBottom>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Controls Section */}
      <Card 
        sx={{ 
          mb: 3, 
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search users..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={handleFilterMenuOpen}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Filters
            </Button>

            <Menu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={handleFilterMenuClose}
            >
              <MenuItem>
                <Typography variant="subtitle2" sx={{ minWidth: 100 }}>User Type:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[UserTypes.ALL, UserTypes.CUSTOMER, UserTypes.CLEANER, UserTypes.ADMIN].map(type => (
                    <Chip
                      key={type}
                      label={type}
                      size="small"
                      color={userTypeFilter === type ? "primary" : "default"}
                      onClick={() => setUserTypeFilter(type)}
                    />
                  ))}
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem>
                <Typography variant="subtitle2" sx={{ minWidth: 100 }}>Status:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[StatusTypes.ALL, StatusTypes.ACTIVE, StatusTypes.INACTIVE, StatusTypes.PENDING].map(status => (
                    <Chip
                      key={status}
                      label={status}
                      size="small"
                      color={statusFilter === status ? "primary" : "default"}
                      onClick={() => setStatusFilter(status)}
                    />
                  ))}
                </Box>
              </MenuItem>
            </Menu>

            <Box sx={{ flexGrow: 1 }} />

            <IconButton sx={{ borderRadius: 2 }}>
              <RefreshIcon />
            </IconButton>
            
            <IconButton sx={{ borderRadius: 2 }}>
              <DownloadIcon />
            </IconButton>

            {selectedUsers.length > 0 && (
              <Chip 
                label={`${selectedUsers.length} selected`} 
                color="primary" 
                variant="outlined"
                onDelete={() => setSelectedUsers([])}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card 
        sx={{ 
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                    checked={filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">User</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Type</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Join Date</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Last Active</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="600">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => {
                  const isItemSelected = isSelected(user.id);
                  return (
                    <TableRow
                      key={user.id}
                      hover
                      selected={isItemSelected}
                      sx={{ 
                        cursor: 'pointer',
                        '&:last-child td, &:last-child th': { border: 0 },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, user.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={getUserTypeIcon(user.type)}
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: theme.palette.background.paper,
                                border: `2px solid ${theme.palette.background.paper}`,
                                borderRadius: '50%',
                                padding: 0.5,
                                '& svg': {
                                  fontSize: '0.8rem',
                                  color: theme.palette.primary.main
                                }
                              }
                            }}
                          >
                            <Avatar src={user.avatar} alt={user.name}>
                              {user.name.charAt(0)}
                            </Avatar>
                          </Badge>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="600">
                              {user.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.type} 
                          size="small"
                          variant="outlined"
                          color={user.type === 'Admin' ? 'secondary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={user.status}
                          size="small"
                          color={getStatusColor(user.status)}
                          icon={getStatusIcon(user.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(user.joinDate).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(user.lastActive).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={handleActionMenuOpen}
                          sx={{ borderRadius: 1 }}
                        >
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={handleActionMenuClose}>
          <EditIcon sx={{ mr: 1 }} />
          Edit User
        </MenuItem>
        <MenuItem onClick={handleActionMenuClose}>
          <EmailIcon sx={{ mr: 1 }} />
          Send Email
        </MenuItem>
        <MenuItem onClick={handleActionMenuClose}>
          <BlockIcon sx={{ mr: 1 }} />
          Block User
        </MenuItem>
        <MenuItem onClick={handleActionMenuClose} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete User
        </MenuItem>
      </Menu>
    </Box>
  );
}